import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sparkle, SignOut, List, Bell, WarningCircle, CaretDown, CaretRight,
  MagnifyingGlass, Star, Moon, Sun, ClipboardText, X, GearSix,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import AIAssistant from "./AIAssistant";
import { LanguagePicker } from "./LanguagePicker";
import { ErrorBoundary } from "./ErrorBoundary";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";

const RIBBON = [
  { id: "home", label: "Dashboard", links: [{ label: "Dashboard", to: "/dashboard" }] },
  {
    id: "sales", label: "Sales", links: [
      { label: "Sales Quotes", to: "/sales?tab=quotes" },
      { label: "Sales Orders", to: "/sales?tab=orders" },
      { label: "Backorders", to: "/sales?tab=backorders" },
      { label: "Sales Return Orders", to: "/sales?tab=returns" },
      { label: "Sales Invoices", to: "/sales?tab=invoices" },
      { label: "CR/Adj Notes", to: "/sales?tab=creditmemos" },
      { label: "Customers", to: "/sales?tab=customers" },
    ],
  },
  {
    id: "purchases", label: "Purchases", links: [
      { label: "Purchase Quotes", to: "/purchasing?tab=quotes" },
      { label: "Purchase Orders", to: "/purchasing?tab=orders" },
      { label: "Purchase Invoices", to: "/purchasing?tab=invoices" },
      { label: "Vendors", to: "/purchasing?tab=vendors" },
      { label: "Payment Run", to: "/purchasing?tab=invoices&action=payment-run" },
    ],
  },
  {
    id: "posted", label: "Posted Documents", links: [
      { label: "Posted Sales Invoices", to: "/sales?tab=posted&sub=pinv" },
      { label: "Posted CR/Adj Notes", to: "/sales?tab=posted&sub=pcr" },
      { label: "Posted Sales Shipments", to: "/sales?tab=posted&sub=ship" },
      { label: "Sales Quote Archives", to: "/sales?tab=posted&sub=aq" },
      { label: "Sales Order Archives", to: "/sales?tab=posted&sub=ao" },
      { label: "Return Order Archives", to: "/sales?tab=posted&sub=ar" },
      { label: "Deleted Invoices", to: "/sales?tab=posted&sub=del" },
      { label: "Posted Purchase Receipts", to: "/purchasing?tab=posted&sub=receipts" },
    ],
  },
  {
    id: "finance", label: "Finance", links: [
      { label: "Chart of Accounts", to: "/finance?tab=accounts" },
      { label: "Journal Entries", to: "/finance?tab=journals" },
      { label: "Bank Reconciliation", to: "/reconciliation" },
    ],
  },
  { id: "items", label: "Items", links: [{ label: "Items", to: "/inventory" }] },
  { id: "projects", label: "Projects", links: [{ label: "Projects", to: "/projects" }, { label: "Stock Transfers", to: "/stock-transfers" }] },
  {
    id: "analysis", label: "Analysis", links: [
      { label: "Trial Balance", to: "/reports?tab=trial" },
      { label: "Profit & Loss", to: "/reports?tab=pnl" },
      { label: "Balance Sheet", to: "/reports?tab=bs" },
      { label: "Aged Receivables", to: "/reports?tab=aged" },
      { label: "Aged Payables", to: "/reports?tab=agedpay" },
      { label: "Cash Calendar", to: "/reports?tab=cashcal" },
      { label: "BAS (GST)", to: "/reports?tab=bas" },
    ],
  },
  { id: "admin", label: "Administration", adminOnly: true, links: [{ label: "Company Profile", to: "/company-profile" }, { label: "Team & Roles", to: "/team" }, { label: "Roles & Permissions", to: "/roles-matrix" }, { label: "Audit Log", to: "/audit-log" }, { label: "Exchange Rates", to: "/fx-rates" }, { label: "Approvals", to: "/approvals" }, { label: "Locations", to: "/locations" }, { label: "Bank Accounts", to: "/bank" }, { label: "Data Integrity", to: "/data-integrity" }, { label: "Draft Cleanup", to: "/draft-cleanup" }] },
];

const ROLE_LABEL = {
  admin: "Administrator", accountant: "Accountant", sales: "Sales",
  purchasing: "Purchasing", viewer: "Viewer",
};
const SEV_DOT = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-blue-500" };
const GROUP_CAP = { sales: "sales.view", purchases: "purchasing.view", finance: "finance.view", projects: "projects.view", analysis: "reports.view" };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [aiOpen, setAiOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [q, setQ] = useState("");
  const [searchFocus, setSearchFocus] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const { data: permData } = useQuery({ queryKey: ["permissions"], queryFn: async () => (await api.get("/permissions")).data, staleTime: 60000 });
  const myCaps = permData?.my_caps;
  const groups = RIBBON.filter((g) => (!g.adminOnly || user?.role === "admin") && (!myCaps || !GROUP_CAP[g.id] || myCaps.includes(GROUP_CAP[g.id])) && (!myCaps || g.id !== "posted" || myCaps.includes("sales.view") || myCaps.includes("purchasing.view")))
    .map((g) => (g.id === "admin" && user?.is_accounts_admin ? { ...g, links: [{ label: "Accounts", to: "/accounts" }, ...g.links] } : g));
  const activeKey = location.pathname + (location.search || "");

  // ----- favorites -----
  const { data: favorites = [] } = useQuery({ queryKey: ["favorites"], queryFn: async () => (await api.get("/favorites")).data });
  const favByTo = Object.fromEntries(favorites.map((f) => [f.to, f]));
  const addFav = useMutation({ mutationFn: async (f) => (await api.post("/favorites", f)).data, onSuccess: () => qc.invalidateQueries({ queryKey: ["favorites"] }) });
  const removeFav = useMutation({ mutationFn: async (id) => api.delete(`/favorites/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["favorites"] }) });
  const toggleFav = (link) => { const ex = favByTo[link.to]; if (ex) removeFav.mutate(ex.id); else addFav.mutate({ label: link.label, to: link.to }); };

  const favGroup = { id: "favorites", label: "Favorites", star: true, links: favorites.map((f) => ({ label: f.label, to: f.to })) };
  const displayGroups = [...groups, favGroup];

  const pathGroup = (
    groups.find((g) => g.links.some((l) => l.to === activeKey))
    || groups.find((g) => g.links.some((l) => l.to.split("?")[0] === location.pathname))
    || { id: "home" }
  ).id;
  const [selected, setSelected] = useState(pathGroup);
  const selectedGroup = displayGroups.find((g) => g.id === selected) || displayGroups[0];

  const go = (to) => { navigate(to); setMobileMenu(false); setQ(""); setSearchFocus(false); };

  // ----- global search -----
  const { data: searchData } = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(q)}`)).data,
    enabled: q.trim().length >= 2,
  });
  const results = searchData?.results || [];

  // ----- breadcrumb -----
  const crumb = (() => {
    const sp = new URLSearchParams(location.search);
    const ct = sp.get("tab"); const csub = sp.get("sub");
    for (const g of groups) for (const l of g.links) {
      const [lp, lq] = l.to.split("?");
      if (lp !== location.pathname) continue;
      const lsp = new URLSearchParams(lq || "");
      const lt = lsp.get("tab"); const lsub = lsp.get("sub");
      if ((lt || null) === (ct || null) && (lsub ? lsub === csub : true)) return { group: g.label, page: l.label };
    }
    for (const g of groups) for (const l of g.links) if (l.to.split("?")[0] === location.pathname) return { group: g.label, page: l.label };
    return null;
  })();

  const { data: alertsData } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
    refetchInterval: 60000,
  });
  const alertCount = alertsData?.total || 0;
  const unreadCount = alertsData?.unread || 0;
  const highCount = alertsData?.unread_counts?.high || 0;
  const markAllRead = useMutation({
    mutationFn: async () => (await api.post("/alerts/read-all")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const dismissAlert = useMutation({
    mutationFn: async (key) => (await api.post("/alerts/read", { key })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const [showAlertPrefs, setShowAlertPrefs] = useState(false);
  const { data: alertPrefs } = useQuery({
    queryKey: ["alert-preferences"],
    queryFn: async () => (await api.get("/alert-preferences")).data,
  });
  const savePrefs = useMutation({
    mutationFn: async (disabled) => (await api.put("/alert-preferences", { disabled })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); qc.invalidateQueries({ queryKey: ["alert-preferences"] }); },
  });
  const toggleAlertType = (t) => {
    const disabled = (alertPrefs?.types || []).filter((x) => x.type === t ? x.enabled : !x.enabled).map((x) => x.type);
    savePrefs.mutate(disabled);
  };

  const { data: myApprovalsData } = useQuery({
    queryKey: ["my-approvals"],
    queryFn: async () => (await api.get("/my-approvals")).data,
    refetchInterval: 60000,
  });
  const approvalCount = myApprovalsData?.count || 0;

  const isLinkActive = (to) => activeKey === to || (to.includes("?") && activeKey.startsWith(to));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        {/* Row 1: brand + menu group captions + search + right actions */}
        <div className="min-h-14 flex items-center gap-3 px-4 sm:px-6 py-1.5">
          <button onClick={() => go("/dashboard")} className="flex items-center gap-2 shrink-0" data-testid="brand-home">
            <img src="/nucleus-logo.png" alt="Neuclius" className="h-9 w-9 object-contain" />
            <span className="font-black tracking-tight text-[15px] hidden sm:inline">Neuclius</span>
          </button>

          <div className="h-6 w-px bg-border hidden lg:block" />

          <nav className="hidden lg:flex items-center flex-wrap gap-x-0.5 gap-y-1 flex-1 min-w-0" data-testid="ribbon-groups">
            {displayGroups.map((g) => (
              <button
                key={g.id}
                data-testid={`ribbon-group-${g.id}`}
                onClick={() => { setSelected(g.id); if (g.links.length === 1 && !g.star) go(g.links[0].to); }}
                className={`px-2.5 py-1.5 text-sm font-semibold rounded-sm inline-flex items-center gap-1 whitespace-nowrap transition-colors duration-150 ${
                  selected === g.id ? "text-primary bg-primary/5" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
                }`}
              >
                {g.star && <Star size={13} weight="fill" className="text-amber-500" />}
                {g.label}
                {g.links.length > 1 && <CaretDown size={11} weight="bold" className="opacity-50" />}
              </button>
            ))}
          </nav>

          <div className="flex-1 lg:hidden" />

          {/* Global search */}
          <div className="relative hidden lg:block shrink-0" data-testid="global-search">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setSearchFocus(true)}
              onBlur={() => setTimeout(() => setSearchFocus(false), 150)}
              onKeyDown={(e) => { if (e.key === "Enter" && results.length) go(results[0].to); if (e.key === "Escape") { setQ(""); setSearchFocus(false); } }}
              placeholder="Tell me what you need…"
              className="w-56 xl:w-64 h-9 pl-8 pr-3 rounded-sm border border-border bg-card text-sm outline-none focus:border-primary transition-colors"
            />
            {searchFocus && q.trim().length >= 2 && (
              <div className="absolute right-0 mt-1 w-80 max-h-96 overflow-y-auto bg-card border border-border shadow-lg z-50 rounded-sm" data-testid="search-results">
                {results.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No matches for “{q}”.</div>}
                {results.map((r, i) => (
                  <button
                    key={i}
                    data-testid="search-result-item"
                    onMouseDown={(e) => { e.preventDefault(); go(r.to); }}
                    className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-secondary transition-colors border-b border-border last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.label}</p>
                      {r.sub && <p className="text-xs text-muted-foreground truncate">{r.sub}</p>}
                    </div>
                    <span className="text-[10px] uppercase font-bold text-muted-foreground border border-border px-1.5 py-0.5 rounded-sm shrink-0">{r.type}</span>
                  </button>
                ))}
                {results.length > 0 && (
                  <button
                    data-testid="search-view-all"
                    onMouseDown={(e) => { e.preventDefault(); go(`/search?q=${encodeURIComponent(q.trim())}`); }}
                    className="w-full text-center px-3.5 py-2.5 text-xs font-semibold text-primary hover:bg-primary/5 border-t border-border sticky bottom-0 bg-card"
                  >
                    View all results for “{q.trim()}”
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              onClick={() => go("/my-approvals")} data-testid="my-approvals-nav"
              className="relative p-2 text-muted-foreground hover:text-foreground transition-colors duration-150" title="My Approvals"
            >
              <ClipboardText size={20} weight={approvalCount ? "fill" : "regular"} />
              {approvalCount > 0 && (
                <span data-testid="my-approvals-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center bg-primary">
                  {approvalCount}
                </span>
              )}
            </button>
            <LanguagePicker />
            <button
              onClick={() => setDark((d) => !d)} data-testid="theme-toggle"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-150" title={dark ? "Light mode" : "Dark mode"}
            >
              {dark ? <Sun size={20} weight="fill" /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setAlertsOpen(true)} data-testid="alerts-bell"
              className="relative p-2 text-muted-foreground hover:text-foreground transition-colors duration-150" title="Alerts"
            >
              <Bell size={20} weight={unreadCount ? "fill" : "regular"} />
              {unreadCount > 0 && (
                <span data-testid="alerts-count" className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${highCount ? "bg-red-500" : "bg-amber-500"}`}>
                  {unreadCount}
                </span>
              )}
            </button>
            <button onClick={() => setAiOpen(true)} data-testid="topbar-ai-button" className="hidden sm:inline-flex items-center h-9 px-2 text-sm font-semibold text-foreground hover:text-primary transition-colors">
              <Sparkle size={16} weight="fill" className="mr-1.5 text-primary" /> Atlas AI
            </button>
            <div className="hidden md:flex items-center gap-2 pl-2 sm:pl-3 border-l border-border">
              <div className="text-right hidden xl:block">
                <p className="text-sm font-semibold leading-none">{user?.name || user?.email}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{ROLE_LABEL[user?.role] || user?.role}</p>
              </div>
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-9 w-9 rounded-sm object-cover" />
              ) : (
                <div className="h-9 w-9 rounded-sm bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                  {(user?.name || user?.email || "?")[0].toUpperCase()}
                </div>
              )}
              <button onClick={logout} data-testid="logout-button" className="text-muted-foreground hover:text-destructive transition-colors duration-150 ml-1" title="Sign out"><SignOut size={18} /></button>
            </div>
            <button className="lg:hidden p-2" onClick={() => setMobileMenu(true)} data-testid="mobile-nav-toggle"><List size={22} /></button>
          </div>
        </div>

        {/* Row 2: ribbon of quick links for the selected group */}
        <div className="hidden lg:block border-t border-border bg-secondary/30 px-6 py-3" data-testid="ribbon-panel">
          {selectedGroup.links.length === 0 ? (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Star size={14} className="text-amber-500" /> Hover any link and tap the star to pin it to Favorites.</p>
          ) : (
            <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-x-10">
              {selectedGroup.links.map((l) => {
                const fav = favByTo[l.to];
                return (
                  <div key={l.to + l.label} className="group flex items-center gap-1.5 mb-2 break-inside-avoid">
                    <button
                      data-testid={`ribbon-link-${slug(l.label)}`}
                      onClick={() => go(l.to)}
                      className={`text-left text-sm transition-colors duration-150 ${
                        isLinkActive(l.to) ? "text-primary font-bold" : "text-primary/80 hover:text-primary hover:underline"
                      }`}
                    >
                      {l.label}
                    </button>
                    <button
                      data-testid={`fav-toggle-${slug(l.label)}`}
                      onClick={() => toggleFav(l)}
                      title={fav ? "Unpin from Favorites" : "Pin to Favorites"}
                      className={`transition-opacity duration-150 ${fav ? "text-amber-500 opacity-100" : "text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-amber-500"}`}
                    >
                      <Star size={13} weight={fav ? "fill" : "regular"} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Breadcrumb */}
        {crumb && (
          <div className="hidden lg:flex items-center gap-1.5 px-6 py-1.5 border-t border-border text-xs text-muted-foreground" data-testid="breadcrumb">
            {crumb.group !== crumb.page && (
              <>
                <span>{crumb.group}</span>
                <CaretRight size={11} />
              </>
            )}
            <span className="text-foreground font-semibold">{crumb.page}</span>
          </div>
        )}
      </header>

      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} />

      {/* Mobile menu */}
      <Sheet open={mobileMenu} onOpenChange={setMobileMenu}>
        <SheetContent side="left" className="w-80 p-0 flex flex-col" data-testid="mobile-menu">
          <SheetHeader className="px-5 py-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2"><img src="/nucleus-logo.png" alt="" className="h-6 w-6 object-contain" /> Neuclius</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-3">
            {displayGroups.filter((g) => g.links.length > 0).map((g) => (
              <div key={g.id} className="mb-4">
                <p className="overline-label px-2 mb-1 flex items-center gap-1">{g.star && <Star size={11} weight="fill" className="text-amber-500" />}{g.label}</p>
                {g.links.map((l) => (
                  <button
                    key={l.to + l.label}
                    data-testid={`mobile-link-${slug(l.label)}`}
                    onClick={() => go(l.to)}
                    className={`block w-full text-left px-3 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${
                      isLinkActive(l.to) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border space-y-2">
            <button onClick={() => { setAiOpen(true); setMobileMenu(false); }} data-testid="open-ai-assistant" className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm text-sm font-semibold text-foreground hover:bg-secondary">
              <Sparkle size={18} weight="fill" className="text-primary" /> Ask Atlas AI
            </button>
            <div className="flex items-center justify-between px-1">
              <div>
                <p className="text-sm font-semibold leading-none">{user?.name || user?.email}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{ROLE_LABEL[user?.role] || user?.role}</p>
              </div>
              <button onClick={logout} data-testid="mobile-logout-button" className="text-muted-foreground hover:text-destructive flex items-center gap-1 text-sm"><SignOut size={18} /> Sign out</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Alerts */}
      <Sheet open={alertsOpen} onOpenChange={setAlertsOpen}>
        <SheetContent className="w-full sm:max-w-md p-0 flex flex-col" data-testid="alerts-panel">
          <SheetHeader className="px-5 py-4 border-b border-border">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle className="flex items-center gap-2"><Bell size={18} className="text-primary" weight="fill" /> Alerts {unreadCount > 0 && <span className="text-sm text-muted-foreground font-normal">({unreadCount} new)</span>}</SheetTitle>
              <SheetDescription className="sr-only">Business alerts and notifications</SheetDescription>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending} data-testid="alerts-mark-all-read" className="text-xs font-semibold text-primary hover:underline disabled:opacity-50">
                    {markAllRead.isPending ? "Marking…" : "Mark all read"}
                  </button>
                )}
                <button onClick={() => setShowAlertPrefs((v) => !v)} data-testid="alerts-prefs-toggle" title="Alert preferences" className={`p-1 rounded-sm transition-colors ${showAlertPrefs ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                  <GearSix size={17} weight={showAlertPrefs ? "fill" : "regular"} />
                </button>
              </div>
            </div>
          </SheetHeader>
          {showAlertPrefs && (
            <div className="px-5 py-4 border-b border-border bg-secondary/30" data-testid="alerts-prefs-panel">
              <p className="overline-label text-muted-foreground mb-2">Show these alert types</p>
              <div className="space-y-1.5">
                {(alertPrefs?.types || []).map((t) => (
                  <label key={t.type} className="flex items-center gap-2.5 text-sm cursor-pointer py-0.5" data-testid={`alert-pref-${t.type}`}>
                    <input type="checkbox" checked={t.enabled} onChange={() => toggleAlertType(t.type)} className="accent-primary h-4 w-4" data-testid={`alert-pref-checkbox-${t.type}`} />
                    <span className={t.enabled ? "" : "text-muted-foreground"}>{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {alertCount === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                <Bell size={36} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">All clear — no active alerts.</p>
              </div>
            )}
            {(alertsData?.alerts || []).map((a, i) => (
              <div key={i} className={`group px-5 py-3.5 flex items-start gap-3 ${a.read ? "opacity-55" : ""}`} data-testid="alert-item">
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${a.read ? "bg-muted-foreground/40" : SEV_DOT[a.severity]}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold flex items-center gap-1.5">
                    {a.severity === "high" && <WarningCircle size={14} className="text-red-500" weight="fill" />}
                    {a.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.message}</p>
                </div>
                {!a.read && (
                  <button
                    onClick={() => dismissAlert.mutate(a.key)}
                    className="shrink-0 p-1 -mt-1 -mr-1 text-muted-foreground hover:text-foreground rounded-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    title="Dismiss" aria-label="Dismiss notification" data-testid="alert-dismiss"
                  >
                    <X size={15} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
